export type KanjiMathematicsLanguage = "en" | "pt";

export type KanjiMathematicsText = Record<KanjiMathematicsLanguage, string>;

export const kanjiMathematicsExplanationTypes = ["structural", "mnemonic", "phonetic"] as const;

export type KanjiMathematicsExplanationType = (typeof kanjiMathematicsExplanationTypes)[number];

export const kanjiMathematicsCategories = ["nature", "family", "people", "numbers"] as const;

export type KanjiMathematicsCategory = (typeof kanjiMathematicsCategories)[number];

export type KanjiMathematicsJlptLevel = "N5" | "N4" | "N3" | "N2" | "N1";

export interface KanjiMathematicsPart {
    symbol: string;
    hiragana: string;
    romaji: string;
    meaning: KanjiMathematicsText;
}

export interface KanjiMathematicsResult extends KanjiMathematicsPart {
    gloss: KanjiMathematicsText;
}

export interface KanjiMathematicsEntry {
    id: string;
    order: number;
    title: KanjiMathematicsText;
    category: KanjiMathematicsCategory;
    components: readonly KanjiMathematicsPart[];
    result: KanjiMathematicsResult;
    explanationType: KanjiMathematicsExplanationType;
    explanation: KanjiMathematicsText;
    note?: KanjiMathematicsText;
    jlptLevel?: KanjiMathematicsJlptLevel;
}

export const featuredKanjiMathematicsId = "two-trees";

const tree: KanjiMathematicsPart = {
    symbol: "木",
    hiragana: "き",
    romaji: "ki",
    meaning: { en: "tree", pt: "árvore" },
};

const woman: KanjiMathematicsPart = {
    symbol: "女",
    hiragana: "おんな",
    romaji: "onna",
    meaning: { en: "woman", pt: "mulher" },
};

const notYet: KanjiMathematicsPart = {
    symbol: "未",
    hiragana: "み",
    romaji: "mi",
    meaning: { en: "not yet", pt: "ainda não" },
};

const entries: readonly KanjiMathematicsEntry[] = [
    {
        id: "two-trees",
        order: 1,
        title: { en: "Two trees", pt: "Duas árvores" },
        category: "nature",
        components: [tree, tree],
        result: {
            symbol: "林",
            hiragana: "はやし",
            romaji: "hayashi",
            meaning: { en: "grove / small woodland", pt: "arvoredo / pequeno bosque" },
            gloss: { en: "grove", pt: "arvoredo" },
        },
        explanationType: "structural",
        explanation: {
            en: "Two trees side by side suggest a little woodland! The kanji is literally written with two 木.",
            pt: "Duas árvores lado a lado sugerem um pequeno bosque! O kanji escreve-se, literalmente, com dois 木.",
        },
        note: {
            en: "Repeating a component is a traditional way of suggesting “many” or “a group”. It is how these particular kanji are built, not a rule that adding meanings explains every kanji.",
            pt: "Repetir um componente é uma forma tradicional de sugerir “muitos” ou “um grupo”. É assim que estes kanji em particular são construídos, e não uma regra segundo a qual somar significados explica todos os kanji.",
        },
        jlptLevel: "N2",
    },
    {
        id: "three-trees",
        order: 2,
        title: { en: "Three trees", pt: "Três árvores" },
        category: "nature",
        components: [tree, tree, tree],
        result: {
            symbol: "森",
            hiragana: "もり",
            romaji: "mori",
            meaning: { en: "forest", pt: "floresta" },
            gloss: { en: "forest", pt: "floresta" },
        },
        explanationType: "structural",
        explanation: {
            en: "Three trees suggest a whole forest! The kanji is literally written with three 木.",
            pt: "Três árvores sugerem uma floresta inteira! O kanji escreve-se, literalmente, com três 木.",
        },
        note: {
            en: "Three copies suggest “a great many” trees. Japanese usually uses 森 for a bigger, denser wood than 林, though the line between them is not strict.",
            pt: "Três cópias sugerem “muitíssimas” árvores. Em japonês, 森 costuma ser maior e mais denso do que 林, embora a fronteira entre os dois não seja rígida.",
        },
        jlptLevel: "N2",
    },
    {
        id: "younger-sister",
        order: 3,
        title: { en: "Younger sister", pt: "Irmã mais nova" },
        category: "family",
        components: [woman, notYet],
        result: {
            symbol: "妹",
            hiragana: "いもうと",
            romaji: "imōto",
            meaning: { en: "younger sister", pt: "irmã mais nova" },
            gloss: { en: "younger sister", pt: "irmã mais nova" },
        },
        explanationType: "mnemonic",
        explanation: {
            en: "Picture a girl (女) who is “not yet” (未) grown up: a younger sister.",
            pt: "Imagine uma rapariga (女) que “ainda não” (未) é adulta: é a irmã mais nova.",
        },
        note: {
            en: "This is a memory story, not the real history of the kanji. Many kanji combine a meaning part and a sound part, and 未 (ミ) is generally explained as the sound part of 妹 (マイ), not as “not yet”.",
            pt: "Esta é uma história para memorizar, não a história real do kanji. Muitos kanji combinam uma parte de significado e uma parte de som, e 未 (ミ) costuma ser explicado como a parte sonora de 妹 (マイ), e não como “ainda não”.",
        },
        jlptLevel: "N4",
    },
];

export const kanjiMathematicsEntries: readonly KanjiMathematicsEntry[] = [...entries].sort((a, b) => a.order - b.order);

export function getKanjiMathematicsEntry(id: string): KanjiMathematicsEntry | undefined {
    return kanjiMathematicsEntries.find((entry) => entry.id === id);
}

export function localizedText(text: KanjiMathematicsText, language: string): string {
    return language === "pt" ? text.pt : text.en;
}

export function formatEquation(entry: KanjiMathematicsEntry): string {
    const left = entry.components.map((component) => component.symbol).join(" + ");
    return `${left} = ${entry.result.symbol}`;
}
